// Default compression settings (used for profile photos, etc.)
export const IMAGE_COMPRESSION = {
  maxWidth: 800,
  maxHeight: 800,
  quality: 0.85,
  format: 'image/jpeg',
}

export interface CompressImageOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
  format?: string
}

/**
 * Compresses an image file before upload to save bandwidth and storage.
 * Maintains aspect ratio while ensuring dimensions don't exceed maxWidth/maxHeight.
 *
 * Pass `options` to override the defaults for a specific call site
 * (e.g. messaging attachments use a larger 1600px long edge to preserve detail).
 */
export const compressImage = async (
  file: File,
  options?: CompressImageOptions,
): Promise<Blob> => {
  const maxWidth = options?.maxWidth ?? IMAGE_COMPRESSION.maxWidth
  const maxHeight = options?.maxHeight ?? IMAGE_COMPRESSION.maxHeight
  const quality = options?.quality ?? IMAGE_COMPRESSION.quality
  const format = options?.format ?? IMAGE_COMPRESSION.format
  return new Promise((resolve, reject) => {
    try {
      // Create an image object
      const img = new Image()

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width
        let height = img.height

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            // Landscape image
            height = Math.round(height * (maxWidth / width))
            width = maxWidth
          } else {
            // Portrait image
            width = Math.round(width * (maxHeight / height))
            height = maxHeight
          }
        }

        // Create canvas for resizing
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        // Draw resized image to canvas
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          URL.revokeObjectURL(img.src)
          reject(new Error('Could not get canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0, width, height)

        // Convert to blob with specified quality
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src)
            if (!blob) {
              reject(new Error('Failed to compress image'))
              return
            }
            resolve(blob)
          },
          format,
          quality,
        )
      }

      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        reject(new Error('Failed to load image for compression'))
      }

      // Load image from File
      img.src = URL.createObjectURL(file)
    } catch (error) {
      reject(error)
    }
  })
}
